import { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Loader2 } from 'lucide-react';
import { generateId } from '../utils/idGenerator';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ProjectChatProps {
  projectId: string;
  projectName: string;
}

export default function ProjectChat({ projectId, projectName }: ProjectChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: `Hello! I'm your AI assistant for ${projectName}. I can help you with questions about tasks, sprints, team members, and project planning. What would you like to know?`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: generateId('chat-message', projectId, 'user'),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Simulate AI response (in production, this would call a backend LLM/RAG service)
    setTimeout(() => {
      const assistantMessage: Message = {
        id: generateId('chat-message', projectId, 'assistant'),
        role: 'assistant',
        content: generateMockResponse(input),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);
    }, 1000);
  };

  const generateMockResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('task') || lowerQuery.includes('todo')) {
      return 'Based on the current project data, you can view all tasks in the Task Board or use the Spreadsheet View for bulk editing. Would you like me to help you find specific tasks or create a new one?';
    }

    if (lowerQuery.includes('sprint')) {
      return 'You can manage sprints from the Sprint Management page. Active sprints can be viewed on the Sprint Board where you can drag and drop tasks between columns. Would you like to know about a specific sprint?';
    }

    if (lowerQuery.includes('team') || lowerQuery.includes('member')) {
      return 'The project team includes developers, stakeholders, and other roles. You can assign tasks to team members from the task details or spreadsheet view. Need help with team assignments?';
    }

    if (lowerQuery.includes('report') || lowerQuery.includes('analytics')) {
      return 'You can generate various reports including weekly summaries, sprint reports, and custom analytics. Reports can be scheduled and sent via email, WhatsApp, or SMS. Would you like to create a report?';
    }

    if (lowerQuery.includes('bug')) {
      return 'Bugs can be tracked in the Bug Tracker page. You can mark bugs as customer-visible and link them to verification resources. Would you like to view or create a bug report?';
    }

    return '🔒 AI/LLM features are available with the Plus plan. Your current Basic plan does not include AI-powered assistance. Upgrade to Plus to unlock intelligent project insights, automated task recommendations, sprint planning assistance, and advanced analytics powered by large language models. Visit Settings → Subscription to upgrade today!';
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-full flex-col rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      {/* Header */}
      <div className="border-b border-border bg-gradient-to-r from-primary/15 to-chart-4/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          <h3 className="font-medium text-foreground">Project Assistant</h3>
          <span className="ml-auto text-xs text-muted-foreground">AI-Powered Q&A</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[400px] max-h-[600px]">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
              }`}
            >
              {message.role === 'user' ? (
                <User className="w-4 h-4" />
              ) : (
                <Bot className="w-4 h-4" />
              )}
            </div>
            <div
              className={`flex-1 ${message.role === 'user' ? 'flex justify-end' : ''}`}
            >
              <div
                className={`inline-block max-w-[80%] rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p
                  className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-primary-foreground/80' : 'text-muted-foreground/80'
                  }`}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
            <div className="rounded-lg bg-muted px-4 py-3">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-border bg-card p-4">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about this project..."
            rows={2}
            className="flex-1 resize-none rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            aria-label="Send message"
            title="Send message"
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          💡 This chat uses AI (placeholder). In production, it will integrate with your backend LLM/RAG service for context-aware responses.
        </p>
      </div>
    </div>
  );
}